import React, { useState } from 'react'
import { observer } from 'mobx-react-lite'

import { useApi } from '../../hooks/storeHook'
import { useMessages } from '../../hooks/snackbarHooks'
import { useBoolState, useLazyEffect } from '../../hooks/commonHooks'

import { Grid } from '@material-ui/core'

import { BoxLoader } from '../BoxLoader'
import { PriceTypeSelect } from '../PriceTypeSelect'

import { entriesOf, keysOf } from '../../common/tsUtils'

import { StoreAccountModel } from '../../types/accountProps'
import { PriceTypesModelBase } from '../../server/mpsklad_core/Models/PriceTypesModelBase'
import { MoySkladApiEntityOption } from '../../server/mpsklad_core/Models/MoySkladApiEntityOption'

export type PriceKey<TPriceModel extends PriceTypesModelBase> =
  string & Exclude<keyof TPriceModel, 'accountId'>

export type PricesConfig<TPriceModel extends PriceTypesModelBase> =
  Record<PriceKey<TPriceModel>, string>

export type PricesRecord<TPriceModel extends PriceTypesModelBase> =
  Record<PriceKey<TPriceModel>, string | undefined>

export type PriceIdsModel<TPriceModel extends PriceTypesModelBase> =
  Pick<PriceTypesModelBase, 'accountId'> & PricesRecord<TPriceModel>

export type PriceTypesMatchingBaseProps<
  TAccount extends StoreAccountModel,
  TPriceModel extends PriceTypesModelBase> = {
  account: TAccount

  onChange: (model: PriceIdsModel<TPriceModel>) => Promise<void>
}

export type PriceTypesMatchingProps<
  TAccount extends StoreAccountModel,
  TPriceModel extends PriceTypesModelBase> =
  PriceTypesMatchingBaseProps<TAccount, TPriceModel>
  & {
  pricesConfig: PricesConfig<TPriceModel>

  initialPriceIds: PricesRecord<TPriceModel>

  tooltips?: Partial<PricesRecord<TPriceModel>>
} & (
  // HACK: Separate prop, because TS doesn't allow assignment of account price props by key from generic constraints
  // (at least the prop itself is enforced)
  Exclude<keyof TPriceModel, keyof PriceTypesModelBase> extends never
  ? {onUpdateAccount?: undefined}
  : {onUpdateAccount: (prices: PricesRecord<TPriceModel>) => void})

export const PriceTypesMatching =
  observer(
    <TAccount extends StoreAccountModel,
      TPriceModel extends PriceTypesModelBase>
    ({
       account,
       pricesConfig,
       initialPriceIds,
       tooltips,
       onChange,
       onUpdateAccount
     }: PriceTypesMatchingProps<TAccount, TPriceModel>) => {
      const api = useApi()
      const {showError, showSuccess} = useMessages()

      const [isLoading, setLoading, setLoaded] = useBoolState(true)
      const [options, setOptions] = useState<MoySkladApiEntityOption[]>([])
      const [priceIds, setPriceIds] = useState(initialPriceIds)

      useLazyEffect(
        async () => {
          setLoading()

          try {
            setOptions(await api.userSync.getMoySkladPriceTypes())
          } finally {
            setLoaded()
          }
        })

      if (isLoading) {
        return <BoxLoader/>
      }

      const onSelect =
        (key: PriceKey<TPriceModel>) =>
          async (selectedId: string | undefined) => {
            const otherIds = entriesOf(priceIds)
              .filter(([k]) => k !== key)
              .map(([, v]) => v)

            if (selectedId && otherIds.includes(selectedId)) {
              showError('Типы цен не могут совпадать!')
              return
            }

            setPriceIds(prev => {
              const newPriceIds = {
                ...prev,
                [key]: selectedId
              } satisfies typeof prev

              setTimeout(async () => {
                try {
                  await onChange({
                    ...newPriceIds,
                    accountId: account.id
                  })
                } catch (e) {
                  console.error('Failed to update price ids', e)
                  showError('Не удалось сохранить цены!')
                  return
                }

                account.msPriceId = newPriceIds.msPriceId
                account.msBasePriceId = newPriceIds.msBasePriceId

                // TODO: All props are not enforced here, consider reloading account instead?
                // TODO: (will reload the whole settings component)
                onUpdateAccount?.(newPriceIds)
                showSuccess('Сохранено!')
              })

              return newPriceIds
            })
          }

      return (
        <Grid container spacing={2}>
          {
            keysOf(pricesConfig).map(key =>
              <Grid item xs={12} sm={6} key={key}>
                <PriceTypeSelect
                  label={pricesConfig[key]}
                  placeholder={pricesConfig[key]}
                  tooltip={tooltips?.[key]}
                  isLoading={isLoading}
                  options={options}
                  selectedId={priceIds[key]}
                  onChange={onSelect(key)}
                />
              </Grid>
            )}
        </Grid>
      )
    })